import React, {useState, useEffect} from 'react';
import {Button, Input, Checkbox, message} from 'antd';
import {CheckOutlined, DeleteOutlined, CloseOutlined, CloudDownloadOutlined, OpenAIOutlined} from '@ant-design/icons';
import quizClient from "../../client/QuizClient";
import useTranslation from "../../hooks/useTranslation";


const {TextArea} = Input;

// Основной и переводной списки вариантов идут парами: у пары один ключ, выданный при
// загрузке или добавлении, и переключение языка попадает в те же поля ввода.
let answerSequence = 0;
const nextAnswerKey = () => {
    answerSequence += 1;
    return `answer-${answerSequence}`;
};
const emptyAnswerKeys = () => [nextAnswerKey(), nextAnswerKey(), nextAnswerKey(), nextAnswerKey()];

/**
 * FlagSwitch renders two clickable circles with flags.
 * Clicking a flag toggles the language mode.
 */
const FlagSwitch = ({isTranslationMode, onChange, mainLanguage, translationLanguage, size = 18}) => {
    const containerStyle = {
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #ccc',
        borderRadius: '12px',
        padding: '4px 6px',
        gap: '8px',
        cursor: 'pointer',
    };

    const circleStyle = (selected, flagUrl) => ({
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundImage: flagUrl ? `url(${flagUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: selected ? 'opacity(90%)' : 'grayscale(100%) opacity(75%)',
        // Кнопка рисует свою рамку и отступы — снимаем, кружок задаётся размером и фоном
        border: 'none',
        padding: 0,
        cursor: 'pointer',
    });

    return (
        <div style={containerStyle}>
            <button
                type="button"
                aria-label={mainLanguage.name}
                aria-pressed={!isTranslationMode}
                onClick={() => onChange(false)}
                style={circleStyle(!isTranslationMode, mainLanguage.flag)}
            />
            <button
                type="button"
                aria-label={translationLanguage.name}
                aria-pressed={isTranslationMode}
                onClick={() => onChange(true)}
                style={circleStyle(isTranslationMode, translationLanguage.flag)}
            />
        </div>
    );
};

/**
 * EditQuizForm allows editing of quiz details.
 * Supports both main language and translation mode.
 */
const EditQuizForm = ({
                          quiz,             // Quiz data
                          deckId,
                          onSave,           // Callback for saving quiz data
                          onCancel,         // Callback for canceling the form
                          onDelete,         // Callback for deleting the quiz
                          isTranslationMode, // Boolean flag for translation mode
                          mainLanguage,
                          translationLanguage,
                          onToggleLanguage,  // Callback to toggle language mode
                      }) => {
    const {t} = useTranslation();

    // State for main language question and answers
    const [mainQuestion, setMainQuestion] = useState('');
    // Списки заполняет эффект по quiz ниже — до него форма пуста.
    const [mainAnswers, setMainAnswers] = useState([]);

    // State for translated question and answers
    const [translatedQuestion, setTranslatedQuestion] = useState('');
    const [translatedAnswers, setTranslatedAnswers] = useState([]);

    // Other state variables
    const [answersNotDefinitive, setAnswersNotDefinitive] = useState(false);
    const [correctAnswerCount, setCorrectAnswerCount] = useState(0);
    const [incorrectAnswerCount, setIncorrectAnswerCount] = useState(0);

    // Initialize form state based on quiz prop
    useEffect(() => {
        setMainQuestion(quiz.question || '');
        const loaded = quiz.answers && quiz.answers.length > 0 ? quiz.answers : null;
        const keys = loaded ? loaded.map(() => nextAnswerKey()) : emptyAnswerKeys();
        setMainAnswers(loaded
            ? loaded.map((ans, index) => ({key: keys[index], text: ans.text || '', isCorrect: ans.isCorrect}))
            : keys.map((key) => ({key, text: '', isCorrect: false})));
        setTranslatedQuestion(quiz.translateQuestion || '');
        setTranslatedAnswers(loaded
            ? loaded.map((ans, index) => ({key: keys[index], text: ans.translatedText || '', isCorrect: ans.isCorrect}))
            : keys.map((key) => ({key, text: '', isCorrect: false})));
        setAnswersNotDefinitive(quiz.answersNotDefinitive || false);
        setCorrectAnswerCount(quiz.correctAnswerCount || 0);
        setIncorrectAnswerCount(quiz.incorrectAnswerCount || 0);
    }, [quiz]);

    // Determine current question and answers based on mode
    const question = isTranslationMode ? translatedQuestion : mainQuestion;
    const answers = isTranslationMode ? translatedAnswers : mainAnswers;

    // Handler for changing question input
    const handleQuestionChange = (e) => {
        if (isTranslationMode) {
            setTranslatedQuestion(e.target.value);
        } else {
            setMainQuestion(e.target.value);
        }
    };

    // Handler for changing answer text
    const handleAnswerChange = (index, value) => {
        if (isTranslationMode) {
            const newTranslatedAnswers = [...translatedAnswers];
            newTranslatedAnswers[index].text = value;
            setTranslatedAnswers(newTranslatedAnswers);
        } else {
            const newMainAnswers = [...mainAnswers];
            newMainAnswers[index].text = value;
            setMainAnswers(newMainAnswers);
        }
    };

    // Toggle isCorrect flag for an answer (applies to both modes)
    const handleCheckboxChange = (index) => {
        const newMainAnswers = [...mainAnswers];
        newMainAnswers[index].isCorrect = !newMainAnswers[index].isCorrect;
        setMainAnswers(newMainAnswers);

        const newTranslatedAnswers = [...translatedAnswers];
        newTranslatedAnswers[index].isCorrect = newMainAnswers[index].isCorrect;
        setTranslatedAnswers(newTranslatedAnswers);
    };

    // Handler for adding a new answer (affects both modes)
    const handleAddAnswer = () => {
        const key = nextAnswerKey();
        setMainAnswers(prev => [...prev, {key, text: '', isCorrect: false}]);
        setTranslatedAnswers(prev => [...prev, {key, text: '', isCorrect: false}]);
    };

    // Handler for deleting the last answer (if more than 4 answers)
    const handleDeleteAnswer = () => {
        if (mainAnswers.length > 4) {
            setMainAnswers(prev => prev.slice(0, -1));
            setTranslatedAnswers(prev => prev.slice(0, -1));
        }
    };

    // Toggle answersNotDefinitive flag
    const handleCheckboxDefinitiveChange = () => {
        setAnswersNotDefinitive(!answersNotDefinitive);
    };

    // Save handler: validates and constructs quiz data based on active mode
    const handleSave = async () => {
        if (!question.trim()) {
            message.error(t('quiz.questionRequired'));
            return;
        }

        const filledAnswers = answers.filter(answer => answer.text.trim() !== '');
        if (filledAnswers.length < 2) {
            message.error(t('quiz.minTwoAnswers'));
            return;
        }
        if (!filledAnswers.some(answer => answer.isCorrect)) {
            message.error(t('quiz.needCorrectAnswer'));
            return;
        }

        let quizData = {
            id: quiz.id,  // Передаем ID для обновления
            deckId,
            answersNotDefinitive,
            correctAnswerCount,
            incorrectAnswerCount,
        };

        if (isTranslationMode) {
            quizData = {
                ...quizData,
                translatedQuestion: translatedQuestion,
                question: mainQuestion,
                answers: translatedAnswers.map(ans => ({
                    translatedText: ans.text,
                    text: mainAnswers[translatedAnswers.indexOf(ans)]?.text || '',
                    isCorrect: ans.isCorrect,
                })),
            };
        } else {
            quizData = {
                ...quizData,
                question: mainQuestion,
                translatedQuestion: translatedQuestion,
                answers: mainAnswers.map(ans => ({
                    text: ans.text,
                    translatedText: translatedAnswers[mainAnswers.indexOf(ans)]?.text || '',
                    isCorrect: ans.isCorrect,
                })),
            };
        }

        try {
            await quizClient.updateQuiz(quiz.id, quizData);
            message.success(t('quiz.updated'));
            if (onSave) onSave(quizData); // Вызываем callback
        } catch (error) {
            console.error('Error updating quiz:', error);
            message.error(t('quiz.updateFailed'));
        }
    };

    // Cancel handler
    const handleCancel = () => {
        onCancel();
    };

    const handlePasteTranslation = async () => {
        try {
            // Чтение текста из буфера обмена
            const clipboardText = await navigator.clipboard.readText();
            // Парсинг JSON
            const translationData = JSON.parse(clipboardText);

            // Проверка корректности структуры JSON
            if (translationData?.question && Array.isArray(translationData.answers)) {
                // Обновление переведённого вопроса
                setTranslatedQuestion(translationData.question);

                // Обновление переведённых ответов:
                // Сопоставляем каждый элемент массива answers из JSON с объектом перевода
                const updatedTranslatedAnswers = translationData.answers.map((ans, index) => ({
                    // Ключ пары остаётся: перевод меняет текст, а не сам вариант.
                    key: translatedAnswers[index]?.key ?? mainAnswers[index]?.key ?? nextAnswerKey(),
                    text: ans,
                    // Сохраняем существующий флаг isCorrect, если он уже задан, иначе false
                    isCorrect: translatedAnswers[index] ? translatedAnswers[index].isCorrect : false,
                }));
                setTranslatedAnswers(updatedTranslatedAnswers);

                message.success(t('quiz.translationPasted'));
            } else {
                message.error(t('quiz.translationInvalid'));
            }
        } catch (error) {
            message.error(t('quiz.translationPasteFailed'));
            console.error('Error pasting translation:', error);
        }
    };

    const handleCopyOriginal = async () => {
        try {
            // Формируем объект с оригинальными данными
            const originalData = {
                question: mainQuestion,
                answers: mainAnswers.map(ans => ans.text)
            };
            const jsonData = JSON.stringify(originalData, null, 2);

            // Берем название языка из объекта translationLanguage (если свойство name отсутствует, замените на нужное)
            const language = translationLanguage?.name || 'target language';

            // Инструкция намеренно на английском и не идёт через i18n: её читает
            // языковая модель, которой этот текст вставят в чат, а не пользователь.
            // Перевод инструкции на польский или китайский ухудшил бы результат.
            const finalText = `Translate the JSON content into ${language}, while:
Leaving any prefixes in the "answers" field unchanged (e.g., "A.", "B.", "C.", "D." or "1.", "2.", "3.", "4." if present).
Here is the JSON:
${jsonData}`;

            await navigator.clipboard.writeText(finalText);
            message.success(t('quiz.originalCopied'));
        } catch (error) {
            message.error(t('quiz.originalCopyFailed'));
            console.error('Error copying original:', error);
        }
    };


    return (
        <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '16px'}}>
            {/* Question input */}

            <div style={{position: 'relative'}}>
                <TextArea
                    rows={4}
                    placeholder={t('quiz.questionPlaceholder')}
                    value={question}
                    onChange={handleQuestionChange}
                />
                {isTranslationMode && (
                    <div style={{position: 'absolute', top: 8, right: 8, display: 'flex', gap: '8px'}}>
                        <OpenAIOutlined
                            style={{fontSize: 18, cursor: 'pointer', opacity: 0.2}}
                            onClick={handleCopyOriginal}
                        />
                        <CloudDownloadOutlined
                            style={{fontSize: 18, cursor: 'pointer', opacity: 0.2}}
                            onClick={handlePasteTranslation}
                        />
                    </div>
                )}
            </div>


            {/* Answer inputs */}
            {answers.map((answer, index) => (
                <div key={answer.key} style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', width: '100%'}}>
                        <Checkbox
                            checked={answer.isCorrect}
                            onChange={() => handleCheckboxChange(index)}
                        />
                        <TextArea
                            rows={2}
                            placeholder={t('quiz.answerPlaceholder', {number: index + 1})}
                            value={answer.text}
                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                            style={{flex: 1}}
                        />
                    </div>
                    {index === answers.length - 1 && (
                        <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                            <Button type="text" size="small" onClick={handleAddAnswer} style={{marginLeft: '26px'}}>
                                + {t('quiz.addAnswer')}
                            </Button>
                            {answers.length > 4 && (
                                <Button type="text" size="small" icon={<DeleteOutlined/>} onClick={handleDeleteAnswer}>
                                    {t('common.delete')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            ))}

            {/* Checkbox for non-definitive answers */}
            <Checkbox checked={answersNotDefinitive} onChange={handleCheckboxDefinitiveChange}>
                {t('quiz.answersNotDefinitive')}
            </Checkbox>

            {/* Bottom control row with language switch and action buttons */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px'}}>
                <FlagSwitch
                    isTranslationMode={isTranslationMode}
                    onChange={onToggleLanguage}
                    mainLanguage={mainLanguage}
                    translationLanguage={translationLanguage}
                    size={18}
                />
                <div style={{display: 'flex', gap: '16px'}}>
                    <Button icon={<CloseOutlined/>} onClick={handleCancel} type="default">
                        {t('common.close')}
                    </Button>
                    <Button type="primary" icon={<CheckOutlined/>} onClick={handleSave}>
                        {t('common.save')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default EditQuizForm;
